import React from "react";
import dayjs from "dayjs";
import { useLazyGetLastWorkingDayQuery } from "../../features/masters/holidays/holidaysApi";

const fmt = (d) => d.format("YYYY-MM-DD");
const today = () => dayjs();
// Weeks run Monday..Sunday, the same as the phone app's DatePresetRangeBar
// (`today.weekday - 1`). dayjs's own startOf("week") is Sunday-based, which
// made web "This Week" a different window from the app's - on a Sunday the
// web showed just that one day while the app showed the whole week
// (2026-09-27, "web and flutter app show different figures for collections").
export const startOfWeekMonday = (d) => d.startOf("day").subtract((d.day() + 6) % 7, "day");

// Named presets computed on demand (not memoized against "now" - a preset
// is re-resolved every time it's picked, so a page left open overnight
// still gets today's actual "Today"/"This Week" etc next click) rather than
// stored as fixed dates.
const PRESETS = {
  TODAY: () => [today(), today()],
  YESTERDAY: () => [today().subtract(1, "day"), today().subtract(1, "day")],
  THIS_WEEK: () => [startOfWeekMonday(today()), today()],
  LAST_WEEK: () => [startOfWeekMonday(today()).subtract(7, "day"), startOfWeekMonday(today()).subtract(1, "day")],
  THIS_MONTH: () => [today().startOf("month"), today()],
  LAST_MONTH: () => [today().subtract(1, "month").startOf("month"), today().subtract(1, "month").endOf("month")],
  LAST_7_DAYS: () => [today().subtract(6, "day"), today()],
  LAST_30_DAYS: () => [today().subtract(29, "day"), today()],
  THIS_YEAR: () => [today().startOf("year"), today()],
};

const PRESET_OPTIONS = [
  { value: "TODAY", label: "Today" },
  { value: "YESTERDAY", label: "Yesterday" },
  { value: "THIS_WEEK", label: "This Week" },
  { value: "LAST_WEEK", label: "Last Week" },
  { value: "THIS_MONTH", label: "This Month" },
  { value: "LAST_MONTH", label: "Last Month" },
  { value: "LAST_7_DAYS", label: "Last 7 Days" },
  { value: "LAST_30_DAYS", label: "Last 30 Days" },
  { value: "THIS_YEAR", label: "This Year" },
  { value: "CUSTOM", label: "Custom" },
];

// Shared date-range state, usable by any list/report page. Mirrors the
// Flutter app's DatePresetRangeBar (lib/core/widgets/date_preset_range_bar.dart)
// used on the Collections dashboard - same preset-resolve convention, same
// controlled from/to state, so both platforms feel like the same filter.
//
// "Yesterday" is holiday-aware (2026-09-14): when calendar-yesterday was a Sunday
// or a public holiday it moves to the last working day, via the same server check
// the phone app uses (GET /masters/holidays/last-working-day). The calendar date is
// applied first and replaced once the answer arrives; a failed check simply keeps
// the calendar date. A late answer is ignored if the user has moved to another
// preset meanwhile.
export const useDatePresetRange = (initialPreset = "LAST_30_DAYS") => {
  const [range] = React.useState(() => PRESETS[initialPreset]());
  const [preset, setPresetState] = React.useState(initialPreset);
  const [dateFrom, setDateFrom] = React.useState(fmt(range[0]));
  const [dateTo, setDateTo] = React.useState(fmt(range[1]));
  const [checkWorkingDay] = useLazyGetLastWorkingDayQuery();
  const presetRef = React.useRef(initialPreset);

  const moveYesterdayToWorkingDay = React.useCallback(
    async (calendarDate) => {
      try {
        const answer = await checkWorkingDay(calendarDate, true).unwrap();
        if (answer && !answer.isWorkingDay && presetRef.current === "YESTERDAY") {
          const workingDay = fmt(dayjs(answer.lastWorkingDay));
          setDateFrom(workingDay);
          setDateTo(workingDay);
        }
      } catch {
        // keep calendar-yesterday - a secondary check must never block the filter
      }
    },
    [checkWorkingDay]
  );

  React.useEffect(() => {
    if (initialPreset === "YESTERDAY") moveYesterdayToWorkingDay(fmt(range[0]));
    // run once, for a page that opens on "Yesterday"
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setPreset = (value) => {
    setPresetState(value);
    presetRef.current = value;
    if (value !== "CUSTOM" && PRESETS[value]) {
      const [from, to] = PRESETS[value]();
      setDateFrom(fmt(from));
      setDateTo(fmt(to));
      if (value === "YESTERDAY") moveYesterdayToWorkingDay(fmt(from));
    }
  };

  return { preset, setPreset, dateFrom, setDateFrom, dateTo, setDateTo };
};

// Self-contained "panel" - a row of preset chips plus, only for Custom, the
// two date inputs - so any page can drop this in as one component instead
// of wiring its own dropdown/date-input pair. Chip-row layout matches the
// Flutter DatePresetRangeBar (see useDatePresetRange above) rather than the
// plain <select> this replaces.
const DateRangePicker = ({ preset, setPreset, dateFrom, setDateFrom, dateTo, setDateTo }) => (
  <div className="d-flex flex-column gap-2 border rounded-3 p-2 bg-light flex-shrink-0">
    <div className="d-flex flex-wrap gap-1">
      {PRESET_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={`btn btn-sm rounded-pill ${preset === opt.value ? "btn-primary" : "btn-outline-secondary"}`}
          onClick={() => setPreset(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
    {preset === "CUSTOM" ? (
      <div className="d-flex align-items-center gap-2">
        <input
          type="date"
          className="form-control form-control-sm"
          style={{ width: 135 }}
          value={dateFrom}
          max={dateTo || undefined}
          onChange={(e) => setDateFrom(e.target.value)}
        />
        <span className="text-muted small">to</span>
        <input
          type="date"
          className="form-control form-control-sm"
          style={{ width: 135 }}
          value={dateTo}
          min={dateFrom || undefined}
          onChange={(e) => setDateTo(e.target.value)}
        />
      </div>
    ) : preset === "YESTERDAY" && dateFrom !== fmt(today().subtract(1, "day")) ? (
      <span className="small" style={{ color: "#8a6d1f" }}>
        Yesterday was not a working day — showing {dayjs(dateFrom).format("ddd D MMM YYYY")}
      </span>
    ) : (
      <span className="text-muted small">{dateFrom} to {dateTo}</span>
    )}
  </div>
);

export default DateRangePicker;
